import { useState, useCallback } from 'react';
import {
  FileUploadProgress,
  FileValidationError,
  AVATAR_CONSTRAINTS,
  VALIDATION_MESSAGES,
} from '../types/profile';

interface FileUploadState {
  isUploading: boolean;
  progress: FileUploadProgress | null;
  error: FileValidationError | null;
  preview: string | null;
}

interface UseFileUploadReturn {
  state: FileUploadState;
  validateFile: (file: File) => Promise<boolean>;
  generatePreview: (file: File) => Promise<string>;
  clearPreview: () => void;
  clearError: () => void;
  resetState: () => void;
}

export const useFileUpload = (): UseFileUploadReturn => {
  const [state, setState] = useState<FileUploadState>({
    isUploading: false,
    progress: null,
    error: null,
    preview: null,
  });

  const validateFile = useCallback(async (file: File): Promise<boolean> => {
    setState(prev => ({ ...prev, error: null }));

    // Check file size
    if (file.size > AVATAR_CONSTRAINTS.MAX_SIZE) {
      const error: FileValidationError = {
        type: 'size',
        message: VALIDATION_MESSAGES.FILE_SIZE_EXCEEDED,
      };
      setState(prev => ({ ...prev, error }));
      return false;
    }

    // Check file format
    if (!AVATAR_CONSTRAINTS.ALLOWED_FORMATS.includes(file.type as any)) {
      const error: FileValidationError = {
        type: 'format',
        message: VALIDATION_MESSAGES.INVALID_FORMAT,
      };
      setState(prev => ({ ...prev, error }));
      return false;
    }

    // Additional validation for file extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = AVATAR_CONSTRAINTS.ALLOWED_EXTENSIONS.some(ext => 
      fileName.endsWith(ext)
    );

    if (!hasValidExtension) {
      const error: FileValidationError = {
        type: 'format',
        message: VALIDATION_MESSAGES.INVALID_FORMAT,
      };
      setState(prev => ({ ...prev, error }));
      return false;
    }

    return true;
  }, []);

  const generatePreview = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setState(prev => ({ ...prev, preview: result }));
        resolve(result);
      };
      
      reader.onerror = () => {
        const error: FileValidationError = {
          type: 'unknown',
          message: 'プレビューの生成に失敗しました',
        };
        setState(prev => ({ ...prev, error }));
        reject(error);
      };
      
      reader.readAsDataURL(file);
    });
  }, []);

  const clearPreview = useCallback((): void => {
    setState(prev => ({ ...prev, preview: null }));
  }, []);

  const clearError = useCallback((): void => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const resetState = useCallback((): void => {
    setState({
      isUploading: false,
      progress: null,
      error: null,
      preview: null,
    });
  }, []);

  return {
    state,
    validateFile,
    generatePreview,
    clearPreview,
    clearError,
    resetState,
  };
};

// Utility functions for file handling
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const getFileExtension = (filename: string): string => {
  return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
};

export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

export const createFileFromUrl = async (url: string, filename: string): Promise<File> => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type });
}; 